import { Fragment, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, RouteObject, matchRoutes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSentryRoutes } from "../config/sentry";
import { UserContext, UserContextPayload } from "../context/userContext";
import GlobalSnackbar from "../components/utility/globalSnackbar";
import axios from "axios";
import GlobalPopUp from "../components/utility/globalPopUp";
import GlobalRoundLoader from "../components/utility/globalRoundLoader";
import OnboardingEnd from "../components/popupFiles/onboardingEnd";
import { GenericDataContext, GenericDataContextPayload } from "../context/genericDataContext";
import { isOrganizationRoleAgent, isAgentOrAdmin, ORGANIZATION_STATUS_NAME, getVendorCompanyName, isVendor } from "../utils/constants";
import { IntegrationContext, IntegrationContextPayload } from "../context/integrationContext";
import agent from "../services/api";
import { Helmet } from "react-helmet-async";
import { getVendorField } from "../utils/whiteLabelling";
import previewImage from '../assets/images/global/Previewimage.png';
import favIcon from '../assets/images/global/favicon.svg';
import { aapdujunagadhRoutes, adminRoutes, agentRoutes, loginRoutes, mawhibaRoutes, privacyPolicyRoutes, ruleBasedInboxRoutes, somethingWentWrongPageRoutes, timSafranskyRoutes } from "./routesConstants";
import { connectSocket, reconnectSocketOnNetworkRestored } from "../utils/socketIo";
import { useSocketStore } from "../store/socketStore";
import SocketConnectionLossPopup from "../components/utility/socketConnectionLossPopup";
import GlobalInboundCallPopup from "../components/utility/globalInboundCallPopup";
import GlobalInboundCallNotification from "../components/utility/globalInboundCallNotification";
import GlobalOutboundCallPopup from "../components/utility/globalOutboundCallPopup";
import UserDetailsPopup from "../components/utility/userDetailsPopup";
import { Alert, Box, Dialog, IconButton, useMediaQuery } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import globalStyles from "../assets/styles/global.module.scss";
import { useEndVoiceCallOnUnload } from "../utils/endVoiceCallOnUnload";
import { CommonResponseModel, vendorDataResponse } from "../models/axiosModel";
import { useDynamicManifest } from "../hooks/useDynamicManifest";
import { useGenericStore } from "../store/genericStore";
import { useIntegrationStore } from "../store/integrationStore";
import { useShallow } from "zustand/react/shallow";
import integrationStyles from "../assets/styles/componentStyles/chatbotStyles/integrations.module.scss";
import WabaVerificationRequestPopup from "../components/utility/wabaVerificationRequestPopup";
import IntegrationNotAvailablePopup from "../components/utility/integrationNotAvailablePopup";

declare global {
    interface Window {
        googleTranslateElementInit: () => void;
    }
}

const Routes = () => {
    useDynamicManifest(); //this hook is being used to create dynamic manifest.json for chrome shortcuts dynamic routing.
    const { getGlobalIntegrationTypeList, globalIntegrationTypeList } = useContext(IntegrationContext) as IntegrationContextPayload
    const { UserMe, setFlagChromeDesktop, flagLoading, userDetail, setFlagLoading, resetLoadingOnRouteChange, flagOnboardingSkip, setFlagOnboardingSkip, GetStripeCurrencyTypeGenericList, GetPromptLogFieldGenericList, flagShowCreditsExhausted, setFlagShowCreditsExhausted, showLoginPopup, setShowLoginPopup, setLoaderDetails, loaderDetails, setLoaderFilterDetails, getLoaderDetails, setCountryCode, flagShowScheduleCall, setFlagShowScheduleCall, flagShowOnboardingEndPopup,
        setFlagOnboardingEndPopup, GetOrganizationDetails, organizationDetails, getLanguagesList, customPagesData, GetCustomPages, handleError, setVendorData, vendorData,
        flagUpdateUserDetailPopup, setFlagUpdateUserDetailPopup, flagViewUserDetailPopup, setFlagViewUserDetailPopup, apiRoutesPermissionAssignment, showOrganizationPausedAlert, setShowOrganizationPausedAlert,
        languageList
    } = useContext(
        UserContext
    ) as UserContextPayload;

    const location = useLocation();
    const previousPathnameRef = useRef(location.pathname);
    
    useLayoutEffect(() => {
        const previousPathname = previousPathnameRef.current;
        previousPathnameRef.current = location.pathname;
        if (previousPathname === location.pathname) {
            return;
        }
        // Pages under the same nested layout (e.g. /chatbot/viewchatbot/*) keep that layout mounted, and its requests
        // (loaded once on mount) must keep running, so requests are aborted only when leaving every nested layout of the previous page.
        // The previous page's loader is cleared either way, so it doesn't block the new page
        const currentMatchedRoutes = (matchRoutes(routes, location.pathname) ?? []).map((match) => match.route);
        const flagSharedNestedLayout = (matchRoutes(routes, previousPathname) ?? []).slice(1)
            .some((match) => match.route.children && currentMatchedRoutes.includes(match.route));
        
        // After login, the app-level calls (user, organization, generic lists) start on the login page and must complete on the dashboard
        const flagFromLoginPage = Boolean(matchRoutes(loginRoutes.filter((route) => route.path !== "*"), previousPathname));
        resetLoadingOnRouteChange(flagSharedNestedLayout || flagFromLoginPage);
    }, [location.pathname, resetLoadingOnRouteChange]);

    useEndVoiceCallOnUnload();

    const { snackbars, removeSnackbar, showError, showSuccess } = useGenericStore(useShallow((state) => ({
        snackbars: state.snackbars,
        removeSnackbar: state.removeSnackbar, 
        showError: state.showError,
        showSuccess: state.showSuccess
    })));

    const { flagShowSocketConnectionLossPopup, setFlagShowSocketConnectionLossPopup } = useSocketStore();

    const { flagOpenWabaVerificationForm, setFlagOpenWabaVerificationForm, flagIntegrationNotAvailablePopup, setFlagIntegrationNotAvailablePopup } = useIntegrationStore(useShallow((state) => ({
        flagOpenWabaVerificationForm: state.flagOpenWabaVerificationForm,
        setFlagOpenWabaVerificationForm: state.setFlagOpenWabaVerificationForm,
        flagIntegrationNotAvailablePopup: state.flagIntegrationNotAvailablePopup,
        setFlagIntegrationNotAvailablePopup: state.setFlagIntegrationNotAvailablePopup
    })));

    const breakPoints = {
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
    }

    const googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
            "google_translate_element"
        );
    };
    // useEffect(() => {
    //     if (isAdmin(userDetail)) {
    //         // Check if the script already exists
    //         const existingScript = document.querySelector<HTMLScriptElement>(
    //             'script[src="//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"]'
    //         );

    //         if (!existingScript) {
    //             const addScript = document.createElement("script");
    //             addScript.setAttribute(
    //                 "src",
    //                 "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
    //             );
    //             document.body.appendChild(addScript);

    //             window.googleTranslateElementInit = googleTranslateElementInit;
    //         }
    //     }
    // }, [userDetail]);

    const { getFunctioMethodList, getFunctionParamsList, getGenericTimezoneList, GetRouteSourceList, routeSources, functionMethodsList, functionParameterTypeDataList, genericTimezoneList } = useContext(GenericDataContext) as GenericDataContextPayload
    const [flagUserMeCompleted, setFlagUserMeCompleted] = useState(false);
    const navigate = useNavigate()
    const [searchParam] = useSearchParams()
    const lead_id = searchParam.get('lead_id')

    const [routes, setRoutes] = useState<RouteObject[]>([])
    const autheticatedRoutesElement = useSentryRoutes(routes);

    const [showSomethingWentWrongError, setShowSomethingWentWrongError] = useState<boolean>(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        if (userDetail.flagUser == false || userDetail.flag_user_anonymous) {
            if (searchParams.has('chatbot_id')) {
                let sampleChatbotPath = window.location.pathname + window.location.search
                let url = encodeURIComponent(sampleChatbotPath)
                navigate("/chatbot?redirect=" + url);
            }
        } else {
            if (userDetail?.plan_detail !== null && userDetail?.plan_detail?.credit_usage_data && userDetail?.plan_detail?.credit_usage_data !== null && userDetail.plan_detail?.credit_usage_data?.stripe_product_name === "Premium" || userDetail.user_role.user_role_name === "Developer" || userDetail.user_role.user_role_name === "Admin") {
                if (searchParams.has('redirect')) {
                    let url = searchParams.get('redirect') ?? window.location.pathname + window.location.search
                    let urlToNavigate = decodeURIComponent(url)
                    if (searchParams.get('redirect') && searchParams.get('redirect') == "/helpdesk") {
                        window.open(process.env.REACT_APP_HELPDESK_URL, "_self")
                    }
                    else {
                        navigate(urlToNavigate);
                    }
                }
            }
            else if (userDetail?.flagUser && searchParams.get('redirect') && searchParams.get('redirect') == "/helpdesk") {
                window.open(process.env.REACT_APP_HELPDESK_URL, "_self")
            }
        }

        // if(!userDetail.flag_user_anonymous && userDetail.flagUser && (!userDetail?.user_first_name || userDetail?.user_first_name?.trim()?.length == 0)){
        //     setFlagUpdateFirstAndLastNamePopup(true); 
        //     setFlagViewUserDetailPopup(false);
        // }
    }, [userDetail])

    // to reload app once tab is not active for half an hour
    useEffect(() => {
        let hiddenTime: number;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenTime = Date.now();
            } else if (document.visibilityState === 'visible') {
                const currentTime = Date.now();
                if (hiddenTime && (currentTime - hiddenTime > 1800000)) { // 600000 ms = 10 minutes
                    window.location.reload(); // Reload the page
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        // Cleanup function to remove the event listener when the component unmounts
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [])

    useEffect(() => {
        const handleOffline = () => {
            showError("Oops! It looks like you’re offline.");
        }

        const handleOnline = () => {
            showSuccess("You’re online now.");
            // Network is back — immediately attempt to re-establish the socket
            // instead of waiting out the pending reconnection backoff delay.
            reconnectSocketOnNetworkRestored();
        }

        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);
        return () => {
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
        }

    }, [navigate])

    const getUserMeApi = async (vendorData: vendorDataResponse | null) => {
        const response = await UserMe();

        if (response?.status && response?.data?.[0]?.access_token) {
            connectSocket(response.data?.[0]?.access_token, vendorData);
        }
        setFlagUserMeCompleted(true);
    }

    // for userMe call
    useEffect(() => {
        (async () => {
            const res: CommonResponseModel<vendorDataResponse> = await agent.getVendors.get();
            // const res = await agent.getVendorDetails.get(Number(process.env.REACT_APP_VENDOR_ID));      //for local test use this line
            if (res?.status && res?.data) {
                setVendorData(res?.data?.length > 0 ? res?.data?.[0] : null)
                axios.defaults.baseURL = getVendorField(res?.data?.length > 0 ? res?.data?.[0] : null, "backend api", process.env.REACT_APP_BASE_URL);
                getUserMeApi(res?.data?.[0]);
            }
            else {
                setVendorData(null)
                getUserMeApi(null);

                // Show an error page when get vendor data call fails on whitelabel domain.
                const hostName = window.location.hostname;
                const domain = hostName?.split('.')?.slice(-2)?.join(".");

                const allowedDomains = ['chronox.ai', 'chronox.in', 'localhost'];
                
                if(!allowedDomains.includes(domain)){
                    setShowSomethingWentWrongError(true);
                    setRoutes(somethingWentWrongPageRoutes);
                }
            }
        })();
    }, []);

    useEffect(() => {
        if (userDetail.flagUser) {
            if(!genericTimezoneList || genericTimezoneList?.length === 0) getGenericTimezoneList()

            if (!isOrganizationRoleAgent(userDetail)) {
                if(!routeSources || routeSources?.length === 0) GetRouteSourceList()
                if(!functionMethodsList || functionMethodsList?.length === 0) getFunctioMethodList()
                if(!functionParameterTypeDataList || functionParameterTypeDataList?.length === 0) getFunctionParamsList()
                if(!languageList || languageList?.length === 0) getLanguagesList()
                if(!globalIntegrationTypeList || globalIntegrationTypeList?.length === 0) getGlobalIntegrationTypeList()
            }
        }
    }, [userDetail])

    // useEffect(() => {
    //     if (userDetail.flagUser && !isOrganizationRoleAgent(userDetail) &&  !isAgentOrAdmin(userDetail)) {
    //         if (!callRecordsData && window.location.pathname.toLowerCase() !== '/callanalysis' && window.location.pathname.toLowerCase() !== '/callanalysis/callrecords') {
    //             GetCallRecordsApi();
    //         }
    //     }
    // }, [callRecordsData, userDetail])

    useEffect(() => {
        if (userDetail.flagUser) {

            if (!organizationDetails) {
                GetOrganizationDetails();
            }
        }
    }, [organizationDetails, userDetail])

    useEffect(() => {
        if (userDetail.flagUser) {
            if (!customPagesData) {
                GetCustomPages();
            }
        }
    }, [customPagesData, userDetail])

    useEffect(() => {
        const userAgent = navigator.userAgent;
        const isEdge = /Edg/.test(userAgent);
        const isMobile = /Mobi/.test(userAgent);
        if (isMobile) {
            setFlagChromeDesktop(false)
        } else {
            if (isEdge) {
                // Your Edge-specific code here
                setFlagChromeDesktop(false)
            } else if (/Chrome/.test(userAgent)) {
                setFlagChromeDesktop(true)
                // Your Chrome-specific code here
            } else {
                setFlagChromeDesktop(false)
                // Your code for other browsers here
            }
        }
    }, []); // Empty dependency array because navigator.userAgent is constant

    useEffect(() => {
        if (userDetail.flagUser && flagOnboardingSkip !== true) {
            // if(userDetail.flag_onboarded){  // !userDetail.flag_email_verified && userDetail.user_delegate_access_flag  && userDetail.user_delegate_access_data && isAdmin(userDetail.user_delegate_access_data)
            //     setFlagOnboardingSkip(true);
            // }  // If on bording flag gets True from BE. No need to call additional information data.
            // else { 
            (async () => {
                setFlagLoading({ ...flagLoading, flagRoundLoader: true });
                try {
                    const response = await agent.getUserAdditionalInfo.get();
                    if (response.status) {
                        const userAdditionalInfo = response?.data?.filter((module: any) => module.user_additional_information_type_name == "Chatbot")[0].user_additional_information;
                        // ******************* FOR ACCESS AFTER 24 HRS CODE *****************************
                        let userCreatedTime = userDetail.user_registered_timestamp;
                        let creationTime = new Date(userCreatedTime as string);
                        const timeDifference = new Date().getTime() - creationTime.getTime();
                        const is24HoursPassed = timeDifference > 24 * 60 * 60 * 1000;
                        //  ******************* FOR ACCESS AFTER 24 HRS CODE *****************************
                        if ((!userAdditionalInfo.length || userAdditionalInfo.some((ans: any) => ans.user_additional_information_data_value == "" && (ans.user_additional_information_data_other_question_value == "" || ans.user_additional_information_data_other_question_value == null)) || userAdditionalInfo.some((ans: any) => (ans.user_additional_information_data_value == null && (ans.user_additional_information_data_other_question_value == "" || ans.user_additional_information_data_other_question_value == null))))) {
                            setFlagOnboardingSkip(false);
                        }
                        else {
                            setFlagOnboardingSkip(true);
                        }
                    } else {
                        handleError(response);
                    }
                }
                catch (error) {
                    // console.log(error);
                }
                if (userDetail.flagUserFromParaphrase) {
                } else {
                    setFlagLoading({ ...flagLoading, flagRoundLoader: false });
                }
            })()
            // }
        }
    }, [userDetail]);

    // useeffect for adding variable in mouseflow
    useEffect(() => {
        // if (userDetail && userDetail.flagUser && process.env["REACT_APP_ENV"] === 'production') {
        if (userDetail.flag_user_anonymous) {
            // @ts-ignore
            window._mfq.push(["setVariable", "user_assignment_id", userDetail.user_assignment_id])
        } else {
            // console.log("script running")
            // @ts-ignore
            window._mfq.push(["setVariable", "user_email", userDetail.user_email])
        }
        // }
    }, [userDetail])

    // function reloadChatbot() {
    //     // Remove existing chatbot script if present
    //     const existingScript = document.getElementById("writwiz-connector");
    //     if (existingScript) {
    //         existingScript.remove();
    //     }

    //     // Encode updated user details
    //     // @ts-ignore
    //     const cbContent = encodeURIComponent(JSON.stringify(window.userDetail));

    //     // Create a new script element
    //     const script = document.createElement("script");
    //     script.id = "writwiz-connector";
    //     script.src = `https://api.chronox.ai/chatbot.js?integration_token=2FMGEouB1735819890322&cb_content=${cbContent}`;

    //     // Append the script to the body
    //     document.body.appendChild(script);
    // }

    // useEffect(() => {
    //     debugger;
    //     if (userDetail && userDetail.user_id) {


    //         // Store user data in the global window object
    //         // @ts-ignore
    //         window.userDetail = userDetail;

    //         // Function to reload chatbot script

    //         // Reload chatbot script whenever userDetail changes
    //         reloadChatbot();
    //     }
    // }, [userDetail]);


    // for fetching country code
    useEffect(() => {
        const fetchData = async () => {
            try {

                const response = await fetch('https://ipinfo.io?token');
                if (!response.ok) {
                    // throw new Error('Network response was not ok');
                    console.log("response was not ok")
                }
                const jsonData = await response.json();
                if (jsonData && jsonData.country) {
                    setCountryCode(jsonData.country.toLowerCase())
                }
            }
            catch (error) {
                console.log("error", error);
            }
        };
        fetchData();
    }, []);

    useLayoutEffect(() => {
        if (!window.location.search.includes('redirect') && window.location.pathname !== "/login" && window.location.pathname !== "/privacy-policy" && window.location.pathname !== "/terms") {

            if (userDetail.flagUser == false || (userDetail.flag_user_anonymous && !userDetail.flag_email_verified)) {
                let urlToNavigateTo = encodeURIComponent(window.location.pathname + window.location.search)
                navigate(`/login?redirect=${urlToNavigateTo}`)
            }
        }
    }, [userDetail])

    const handleRoutesBasedOnUserRole = () => {
        if(!userDetail?.user_assignment_role_data?.length && isOrganizationRoleAgent(userDetail)){
            setRoutes([...privacyPolicyRoutes, ...agentRoutes(userDetail)]); // Routes for Organization Agent
        } else {
            setRoutes([...privacyPolicyRoutes, ...adminRoutes(customPagesData, lead_id, userDetail, apiRoutesPermissionAssignment, vendorData)]) // Routes other role
        }
    }

    const handleLoginRoutes = () => {
        if(!showSomethingWentWrongError) {
            setRoutes([...privacyPolicyRoutes, ...loginRoutes]);
        } else {
            setRoutes(somethingWentWrongPageRoutes);
        }
    }

    useEffect(() => {
        if (!userDetail?.flagUser || (userDetail.flag_user_anonymous)) {
            handleLoginRoutes();
        } else {
            if (userDetail.flag_email_verified) {
                switch (userDetail?.user_organization_data?.organization_id) {
                    case 2545: // routes for aapdujunagadh
                        setRoutes([...privacyPolicyRoutes, ...aapdujunagadhRoutes(userDetail, apiRoutesPermissionAssignment)]);
                        break;
                    case 2569: // routes for mawhiba
                        setRoutes([...privacyPolicyRoutes, ...mawhibaRoutes(lead_id, userDetail, apiRoutesPermissionAssignment)]);
                        break;
                    // case 2582: //routes for tim safransky 
                    //     setRoutes([...privacyPolicyRoutes, ...timSafranskyRoutes(customPagesData, userDetail, apiRoutesPermissionAssignment)]);
                    //     break;
                    default:
                        handleRoutesBasedOnUserRole();
                        break;
                        // switch (userDetail?.user_organization_data?.user_organization_role_name?.toLowerCase()) {
                        //     case "organization agent":  // Routes for Organization Agent
                        //         setRoutes([...privacyPolicyRoutes, ...agentRoutes(userDetail)]);
                        //         break;
                        //     default: // Routes for Organization Owner, admin and member.
                        //         setRoutes([...privacyPolicyRoutes, ...adminRoutes(customPagesData, lead_id, userDetail, vendorData)])
                        // }

                }
            } else {
                handleLoginRoutes();
            }
        }
    }, [userDetail, customPagesData, lead_id])

    if (flagUserMeCompleted == false) {
        return <GlobalRoundLoader />;
    }
    if (userDetail.flagUser && !userDetail.flag_user_anonymous && userDetail.flag_email_verified && flagOnboardingSkip == null) {
        return <GlobalRoundLoader />
    }

    return (

        <Fragment>
            <Helmet>
                <title>{getVendorField(vendorData, "company meta title", "Chronox AI | Build No-Code AI Agents for Instant Customer Solutions")}</title>
                <meta name="description" content={getVendorField(vendorData, "company meta description", "Elevate your customer interactions with Chronox's intelligent, action-oriented agents. Deploy AI Agents seamlessly across channels, using dynamic messaging and real-time data to boost engagement.")} />
                <link rel="icon" href={getVendorField(vendorData, "company fav icon (ico)", favIcon)} />
                <meta property="og:image" content={getVendorField(vendorData, "company preview image", previewImage)} />
                <link rel="apple-touch-icon" href={getVendorField(vendorData, "company fav icon (ico)", favIcon)} />
            </Helmet>
            {/* <div id="google_translate_element"></div> */}
            {flagShowOnboardingEndPopup &&
                <GlobalPopUp
                    paperMaxwidth={"100%"}
                    open={flagShowOnboardingEndPopup}
                    borderRadius="10px"
                    flagShowCloseIcon={true}
                    handleClose={() => { setFlagOnboardingEndPopup(false) }}>
                    <OnboardingEnd />
                </GlobalPopUp>}
            {flagShowScheduleCall &&
                <GlobalPopUp
                    paperMaxwidth={breakPoints.flagSmallScreen ? "100%" : "55%"}
                    open={flagShowScheduleCall}
                    flagShowCloseIcon={true}
                    margin={"10px"}
                    fullWidth
                    paperMinWidth={breakPoints.flagSmallScreen ? "90%" : "55%"}
                    handleClose={() => { setFlagShowScheduleCall(false) }}
                >
                    <iframe title="Meeting" src={getVendorField(vendorData, "company meeting url", "https://calendly.com/birud-chronox/30min?preview_source=et_card&month=2025-01")} style={{ height: "85vh", maxWidth: breakPoints.flagSmallScreen ? "100%" : "55vw", border: 0 }}></iframe>
                </GlobalPopUp>
            }

            {flagLoading.flagRoundLoader && <GlobalRoundLoader />}  

            {flagUpdateUserDetailPopup &&
                <GlobalPopUp
                    open={flagUpdateUserDetailPopup}
                    onClose={() => { setFlagUpdateUserDetailPopup(false); setFlagViewUserDetailPopup(false); }}
                    // flagBackdrop
                    paperMinWidth={breakPoints.flagSmallScreen ? "200px" : "650px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "610px" : "650px"}
                    flagShowCloseIcon={true}
                    titlePadding='0'
                    handleClose={() => { setFlagUpdateUserDetailPopup(false); setFlagViewUserDetailPopup(false); }}
                >
                    <UserDetailsPopup />
                </GlobalPopUp>
            }

            {/* Snackbar */}
            {snackbars?.length > 0 && (
                <div className={globalStyles.snackbarStackContainer}>
                    {snackbars.map((snackbar) => {
                        return (
                            <GlobalSnackbar
                                key={snackbar.id}
                                open={true}
                                type={snackbar.type}
                                message={snackbar.message}
                                hideDuration={5000}
                                handleClose={() => removeSnackbar(snackbar.id)}
                            />
                        );
                    })}
                </div>
            )}

            {flagShowSocketConnectionLossPopup &&
                <GlobalPopUp
                    open={flagShowSocketConnectionLossPopup}
                    onClose={() => { setFlagShowSocketConnectionLossPopup(false); }}
                    handleClose={() => { setFlagShowSocketConnectionLossPopup(false); }}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "500px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "600px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                    flagBackdrop
                >
                    <SocketConnectionLossPopup />
                </GlobalPopUp>
            }
            <GlobalInboundCallNotification />
            <GlobalInboundCallPopup />
            <GlobalOutboundCallPopup />
            {autheticatedRoutesElement}

            {showOrganizationPausedAlert && (
                <Box className={globalStyles.pauseOrganizationAlertBox}>
                    <Alert severity="warning" onClose={() => { setShowOrganizationPausedAlert(false) }}>
                        Your organization is temporarily paused. Please contact{" "}
                        {isVendor(vendorData, userDetail)
                            ? getVendorCompanyName(vendorData)
                            : (<a href="https://chronox.ai/contact-us" target="_blank" rel="noopener noreferrer">Chronox AI</a>)
                        }{" "}
                        support for further assistance.
                    </Alert>
                </Box>
            )}

            {flagOpenWabaVerificationForm && 
                <GlobalPopUp
                    open={flagOpenWabaVerificationForm}
                    onClose={() => { setFlagOpenWabaVerificationForm(false); }}
                    handleClose={() => { setFlagOpenWabaVerificationForm(false); }}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "700px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "800px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                    flagShowCloseIcon
                >
                    <WabaVerificationRequestPopup />
                </GlobalPopUp>
            } 

            {flagIntegrationNotAvailablePopup && 
                <GlobalPopUp
                    open={flagIntegrationNotAvailablePopup}
                    onClose={() => { setFlagIntegrationNotAvailablePopup(false); }}
                    handleClose={() => { setFlagIntegrationNotAvailablePopup(false); }}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "600px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "610px" : "650px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                    flagShowCloseIcon
                >
                    <IntegrationNotAvailablePopup />
                </GlobalPopUp>
            }

        </Fragment>
    );
};

export default Routes;
